import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import * as db from '../services/db'
import { playCheckoutSound } from '../services/sounds'
import { hapticLight, hapticSuccess } from '../utils/haptic'

const CELEBRATION_KEY = 'celebrationEnabled'

const BILLS = [
  { label: '$20', valueCents: 2000 },
  { label: '$10', valueCents: 1000 },
  { label: '$5', valueCents: 500 },
  { label: '$1', valueCents: 100 },
] as const

const COINS = [
  { label: '25¢', valueCents: 25 },
  { label: '10¢', valueCents: 10 },
  { label: '5¢', valueCents: 5 },
  { label: '1¢', valueCents: 1 },
] as const

function changeInWords(changeCents: number): string {
  if (changeCents <= 0) return ''
  const dollars = Math.floor(changeCents / 100)
  const cents = changeCents % 100
  const parts: string[] = []
  if (dollars > 0) parts.push(dollars === 1 ? '1 dollar' : `${dollars} dollars`)
  if (cents > 0) parts.push(cents === 1 ? '1 cent' : `${cents} cents`)
  return parts.join(' and ')
}

interface LocationState {
  receiptId: string
  changeCents: number
}

export function PaymentChangeView() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState | null
  const [countedCents, setCountedCents] = useState(0)
  const [celebrationEnabled, setCelebrationEnabled] = useState(true)

  const receiptId = state?.receiptId ?? ''
  const changeCents = state?.changeCents ?? 0
  const isCorrect = changeCents > 0 && countedCents === changeCents

  useEffect(() => {
    db.getSetting(CELEBRATION_KEY).then((v) => setCelebrationEnabled(v !== 'false'))
  }, [])

  useEffect(() => {
    if (!receiptId && !state) {
      navigate('/')
    }
  }, [receiptId, state, navigate])

  const addCounted = (cents: number) => {
    hapticLight()
    setCountedCents((c) => Math.min(changeCents, c + cents))
  }

  const handleDone = () => {
    hapticSuccess()
    playCheckoutSound()
    if (celebrationEnabled) {
      import('canvas-confetti').then((mod) => {
        try {
          mod.default({ particleCount: 100, spread: 70, origin: { y: 0.6 } })
        } catch {
          // ignore
        }
      })
    }
    setTimeout(() => navigate(`/receipt/${receiptId}`), 800)
  }

  if (!receiptId) {
    return null
  }

  if (changeCents === 0) {
    return (
      <div className="page page--payment page--change">
        <h1 className="page-title">Perfect! 💵</h1>
        <div className="payment-change-card">
          <p className="payment-change-big">No change needed!</p>
          <p className="payment-change-sub">The customer paid the exact amount.</p>
        </div>
        <button type="button" className="btn-large btn-primary" onClick={handleDone}>
          Done →
        </button>
      </div>
    )
  }

  return (
    <div className="page page--payment page--change">
      <h1 className="page-title">Give the change back 💵</h1>
      <p className="payment-cash-instruction">
        Tap the bills and coins to count out the change for the customer.
      </p>
      <div className="payment-change-target-card">
        <p className="payment-cash-given-label">Change to give</p>
        <p className="payment-cash-given-amount">${(changeCents / 100).toFixed(2)}</p>
        <p className="payment-cash-change-words">{changeInWords(changeCents)}</p>
      </div>
      <div className="payment-cash-money">
        <p className="payment-cash-section-label">Bills</p>
        <div className="payment-cash-row">
          {BILLS.map((b) => (
            <button
              key={b.label}
              type="button"
              className="payment-cash-coin-btn payment-cash-bill"
              onClick={() => addCounted(b.valueCents)}
              aria-label={`Add ${b.label}`}
              disabled={countedCents >= changeCents}
            >
              {b.label}
            </button>
          ))}
        </div>
        <p className="payment-cash-section-label">Coins</p>
        <div className="payment-cash-row">
          {COINS.map((c) => (
            <button
              key={c.label}
              type="button"
              className="payment-cash-coin-btn payment-cash-coin"
              onClick={() => addCounted(c.valueCents)}
              aria-label={`Add ${c.label}`}
              disabled={countedCents >= changeCents}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="payment-cash-given-card">
        <p className="payment-cash-given-label">You&apos;ve counted out</p>
        <p className="payment-cash-given-amount">${(countedCents / 100).toFixed(2)}</p>
      </div>
      {isCorrect && (
        <div className="payment-change-success">
          <p className="payment-change-success-text">That&apos;s right! ✓</p>
          <button type="button" className="btn-large btn-primary" onClick={handleDone}>
            Done →
          </button>
        </div>
      )}
    </div>
  )
}
